<script setup lang="ts">
/**
 * Allotment of Shares — Bonus Issue (M32203) — 8문서 패키지를 작성 흐름 4단계(스텝퍼)로 렌더.
 * · 모든 문서를 마운트 유지(v-show) → 단계 이동해도 입력값 보존. 필드 키는 문서 간 공유(ctx values).
 * · Stage 4 하단 = Create/Update Task (가짜 "All stages complete" 제거).
 */
import { computed, ref } from 'vue';
import type { Component } from 'vue';
import { BONUS_DOCS, BONUS_STAGES, type BonusDocCode } from './bonusFields';
import BonusAuthorityDoc from './BonusAuthorityDoc.vue';
import BonusShareApplicationDoc from './BonusShareApplicationDoc.vue';
import BonusLetterDoc from './BonusLetterDoc.vue';
import BonusIncreaseDoc from './BonusIncreaseDoc.vue';
import BonusS76DirectorDoc from './BonusS76DirectorDoc.vue';
import BonusS76SecretaryDoc from './BonusS76SecretaryDoc.vue';
import BonusStatementDoc from './BonusStatementDoc.vue';
import BonusS78ReturnDoc from './BonusS78ReturnDoc.vue';

const props = withDefaults(defineProps<{
  studio?: boolean;
  editMode?: boolean;
  creating?: boolean;
}>(), {
  studio: false,
  editMode: false,
  creating: false,
});

const emit = defineEmits<{
  (e: 'create-task'): void;
}>();

const DOC_COMPONENTS: Record<BonusDocCode, Component> = {
  BON01: BonusAuthorityDoc,
  BON02: BonusShareApplicationDoc,
  BON03: BonusLetterDoc,
  BON04: BonusIncreaseDoc,
  BON05: BonusS76DirectorDoc,
  BON06: BonusS76SecretaryDoc,
  BON07: BonusStatementDoc,
  BON08: BonusS78ReturnDoc,
};

const currentStage = ref(1);
function go(n: number) { if (n >= 1 && n <= BONUS_STAGES.length) currentStage.value = n; }

const view = computed(() => {
  let n = 0;
  return BONUS_STAGES.map((st) => {
    const docs = (Object.values(BONUS_DOCS))
      .filter((d) => d.stage === st.no)
      .map((d) => ({ ...d, comp: DOC_COMPONENTS[d.docCode], no: (n += 1) }));
    return { ...st, docs };
  });
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));
const createLabel = computed(() => (props.editMode ? 'Update Task' : 'Create Task'));
</script>

<template>
  <div class="allot-pkg">
    <!-- 단계 스텝퍼 -->
    <div class="stepper">
      <button
        v-for="st in view" :key="st.no"
        class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }"
        :title="`Stage ${st.no} · ${st.en}`" @click="go(st.no)"
      >
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.en }}</small></span>
      </button>
    </div>

    <!-- 각 단계 (현재 단계만 표시 · 나머지는 마운트 유지) -->
    <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
      <div class="stage-head">
        <span class="stage-badge">Stage {{ st.no }}</span>
        <span class="stage-t">{{ st.en }}</span>
      </div>

      <div v-for="d in st.docs" :key="d.docCode" class="pkg-doc">
        <div class="pkg-doclabel">
          <span class="ic">{{ d.icon }}</span>
          <span class="nm">{{ d.label }}</span>
          <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
        </div>
        <div class="pkg-paper"><component :is="d.comp" /></div>
      </div>
    </section>

    <!-- 단계 이동 -->
    <div class="stage-nav">
      <button v-if="currentStage > 1" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ BONUS_STAGES.length }}</span>
      <button
        v-if="currentStage < BONUS_STAGES.length"
        class="nav-btn primary"
        @click="go(currentStage + 1)"
      >Next →</button>
      <button
        v-else-if="!studio"
        class="nav-btn primary"
        :disabled="creating"
        @click="emit('create-task')"
      >{{ creating ? (editMode ? 'Updating…' : 'Creating…') : createLabel }}</button>
    </div>
  </div>
</template>

<!-- 문서 공용 스타일 (전역) -->
<style src="./bonusDoc.css"></style>

<style scoped>
.allot-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 100%; max-width: 900px; margin: 0 auto 18px; padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, .06); }
.step { flex: 1; display: flex; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all .15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stage { width: 100%; max-width: 900px; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper { background: #fff; padding: 34px 38px; box-shadow: 0 4px 24px rgba(20, 30, 60, .12); font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 900px; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>
